import { forwardRef, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from "react";
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ChevronDown12Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import { FuiButton } from "@/components/fui-control";

const usePersonRowStyles = makeStyles({
  trigger: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    maxWidth: "100%",
    width: "100%",
    minWidth: 0,
    minHeight: "36px",
    margin: 0,
    padding: 0,
    paddingRight: "16px",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    fontWeight: tokens.fontWeightRegular,
    color: "inherit",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: "transparent",
    },
    ":hover [data-person-chevron]": {
      opacity: 1,
    },
    ":active": {
      backgroundColor: "transparent",
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    ":focus-visible [data-person-chevron]": {
      opacity: 1,
    },
    "&[aria-expanded='true'] [data-person-chevron]": {
      opacity: 1,
    },
    "& > :not([data-person-chevron])": {
      minWidth: 0,
      flex: "1 1 auto",
    },
  },
  chevron: {
    position: "absolute",
    right: "0px",
    top: "50%",
    transform: "translateY(-50%)",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    display: "inline-flex",
    alignItems: "center",
    opacity: 0,
    transitionProperty: "opacity",
    transitionDuration: tokens.durationFaster,
  },
});

/** 이름 셀용 메뉴 트리거. 하단 꺽쇠는 호버·포커스·메뉴 열림 시에만 보인다. */
export const PersonRowTrigger = forwardRef<
  HTMLButtonElement,
  { label: string; children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>
>(function PersonRowTrigger({ label, children, className, ...rest }, ref) {
  const styles = usePersonRowStyles();
  return (
    <button type="button" ref={ref} className={mergeClasses(styles.trigger, className)} aria-label={label} {...rest}>
      {children}
      <span className={styles.chevron} data-person-chevron aria-hidden="true">
        <ChevronDown12Regular />
      </span>
    </button>
  );
});

export function RowMoreMenu({
  items,
  label = "더 보기",
  children,
}: {
  items: Array<{ label: string; onClick: () => void }>;
  label?: string;
  children?: ReactElement;
}) {
  if (items.length === 0) return children ?? null;
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        {children ?? <FuiButton appearance="subtle" density="dialog" icon={<MoreHorizontal16Regular />} aria-label={label} />}
      </MenuTrigger>
      <MenuPopover onClick={(e) => e.stopPropagation()}>
        <MenuList>
          {items.map((it) => (
            <MenuItem key={it.label} onClick={it.onClick}>
              {it.label}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
